import type { FigmaNodeData } from '@/types/node';
import type { ConnectedFigmaFrame, EditState, ImageFile, FigmaNodeSnapshot, Node as WasmNode } from 'web';

type FigmaLiveFields = {
  snapshot: FigmaNodeSnapshot | undefined;
  outputImage: ImageFile | undefined;
  connectedFigmaFrame: ConnectedFigmaFrame | undefined;
};

type UpdateNodeEditState = Extract<EditState, { type: 'update_node' }>;

function liveFieldsFromData(data: FigmaNodeData | undefined): FigmaLiveFields | undefined {
  if (!data || data.type !== 'figma_node') return undefined;
  return {
    snapshot: data.snapshot,
    outputImage: data.outputImage,
    connectedFigmaFrame: data.connectedFigmaFrame,
  };
}

/**
 * Keep live sync timestamps when restoring a historical connected frame. Identity
 * (`fileKey` / `nodeId`) still comes from history; polling can move timestamps forward
 * independently of input undo/redo.
 */
function withLiveConnectedFrameTimestamps(
  historical: ConnectedFigmaFrame | undefined,
  live: ConnectedFigmaFrame | undefined,
): ConnectedFigmaFrame | undefined {
  if (!historical || !live) return historical;
  return {
    ...historical,
    lastSyncedAt: live.lastSyncedAt,
    lastEditedAt: live.lastEditedAt,
  };
}

/**
 * FigmaNode-only: keep live preview (`snapshot` / `outputImage`) and connected-frame
 * timestamps on a historical wasm `update_node`. Inputs come from history; preview should
 * only change after Pixie update-variables finishes.
 *
 * Caller must pass an `update_node` whose kind is already `figma_node`.
 */
export function getFigmaNodeEditState(
  editState: UpdateNodeEditState,
  getCurrentData: (nodeId: string) => FigmaNodeData | undefined,
): EditState {
  // Keep the wasm NodeKind discriminant as a string literal so TypeScript narrows against
  // designer.d.ts (same pattern as FlowGraph wasm switches).
  if (editState.node.kind.type !== 'figma_node') return editState;

  const live = liveFieldsFromData(getCurrentData(editState.nodeId));
  if (!live) return editState;

  const node: WasmNode = {
    ...editState.node,
    kind: {
      type: 'figma_node',
      data: {
        ...editState.node.kind.data,
        snapshot: live.snapshot,
        outputImage: live.outputImage,
        connectedFigmaFrame: withLiveConnectedFrameTimestamps(
          editState.node.kind.data.connectedFigmaFrame,
          live.connectedFigmaFrame,
        ),
      },
    },
  };

  return { ...editState, node };
}
